import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import {
  postTrackingSessionSummary,
  postTrackingTechnicalSnapshot,
} from '../../axios/Utils/tracking';
import { useUser } from '../../context/UserContext';
import {
  ActiveTrackingSessionSnapshot,
  NotificationPermissionStatus,
  TrackingAggregate,
  TrackingPendingQueue,
  TrackingSessionEndReason,
  TrackingSessionSummary,
  TrackingTechnicalPendingQueue,
  TrackingTechnicalSnapshot,
} from '../../types/Tracking/types';
import {
  NOTIFICATION_PERMISSION_EVENTS,
  notificationPermissionEvents,
} from '../../utils/notificationPermissionEvents';
import {
  NOTIFICATION_TRACKING_EVENTS,
  notificationTrackingEvents,
} from '../../utils/notificationTrackingEvents';
import { StorageService } from '../../utils/storage';
import { WEB_APP_VERSION, normalizeAppVersion } from '../../utils/appVersion';
import {
  appendPendingTechnicalSnapshot,
  appendPendingTrackingSummary,
  buildActiveSessionSnapshot,
  buildTrackingUserKey,
  createTrackingId,
  debugTrackingLog,
  getTrackingActiveSessionStorageKey,
  getTrackingAggregateStorageKey,
  getTrackingLastSessionStorageKey,
  getTrackingPendingQueueStorageKey,
  getTrackingTechnicalPendingQueueStorageKey,
  getTrackingTechnicalSnapshotStorageKey,
  incrementCount,
  isTrackingDiagnosticsSyncEnabled,
  isTrackingSyncEnabled,
  mapPathnameToTrackingScreen,
  mergeSessionIntoAggregate,
  normalizeTrackingPlatform,
  parseStoredTrackingJson,
  registerTrackingLogoutFlushHandler,
  resolveMostUsedKey,
} from '../../utils/tracking';
import {
  attachTrackingDiagnosticsDebugApi,
  getTrackingTechnicalState,
  hydrateTrackingTechnicalState,
  recordTrackingRecentError,
  recordTrackingSuccessfulAction,
  resetTrackingTechnicalState,
  resolveTrackingNetworkStatus,
  setTrackingNetworkStatus,
  setTrackingRouteState,
  subscribeTrackingTechnicalState,
} from '../../utils/trackingDiagnostics';

interface ActiveTrackingSession
  extends Omit<ActiveTrackingSessionSnapshot, 'updatedAt'> {}

const NOTIFICATION_SEEN_TRACKING_KEY = 'notifications->seen';

const incrementCountBy = (
  counts: Record<string, number>,
  key: string,
  amount: number,
): Record<string, number> => ({
  ...counts,
  [key]: (counts[key] ?? 0) + Math.max(amount, 0),
});

const buildNotificationSeenActionLabel = (count: number): string =>
  count > 1 ? `${count} notificaciones vistas` : 'Notificacion vista';

declare global {
  interface Window {
    __fielpetTracking?: {
      status: () => {
        trackingReady: boolean;
        userKey: string;
        technicalSyncStatus: 'enabled' | 'disabled' | 'unsupported';
        activeSession: ActiveTrackingSession | null;
        technicalState: ReturnType<typeof getTrackingTechnicalState>;
      };
    };
  }
}

const TrackingHandler: React.FC = () => {
  const technicalProgressFlushDelayMs = 1500;
  const location = useLocation();
  const { user } = useUser();
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [permissionStatus, setPermissionStatus] =
    useState<NotificationPermissionStatus>('unknown');

  const activeSessionRef = useRef<ActiveTrackingSession | null>(null);
  const appVersionRef = useRef<string>(normalizeAppVersion(WEB_APP_VERSION));
  const isAppActiveRef = useRef<boolean>(
    typeof document === 'undefined' ? true : !document.hidden,
  );
  const lastPermissionResolutionRef = useRef<{
    signature: string;
    at: number;
  } | null>(null);
  const technicalUpdateSuppressedRef = useRef(false);
  const technicalSyncUnsupportedRef = useRef(false);
  const technicalFlushTimeoutRef = useRef<number | null>(null);

  const userEmail = user?.email ?? '';
  const userKey = useMemo(() => buildTrackingUserKey(userEmail), [userEmail]);
  const isNative = Capacitor.isNativePlatform();
  const trackingReady = isNative ? trackingEnabled : Boolean(userKey);

  const persistActiveSessionSnapshot = useCallback(
    async (session: ActiveTrackingSession) => {
      const activeSessionKey = getTrackingActiveSessionStorageKey(session.userKey);
      const snapshot = buildActiveSessionSnapshot({
        sessionId: session.sessionId,
        userKey: session.userKey,
        platform: session.platform,
        appVersion: session.appVersion,
        lastLoginAt: session.lastLoginAt,
        permissionStatus: session.permissionStatus,
        sessionUsageMs: session.sessionUsageMs,
        lastForegroundAtMs: session.lastForegroundAtMs,
        screenCounts: session.screenCounts,
        transitionCounts: session.transitionCounts,
        previousScreen: session.previousScreen,
        lastTrackedPath: session.lastTrackedPath,
        diagnosticsRevision: session.diagnosticsRevision,
      });

      await StorageService.set(activeSessionKey, JSON.stringify(snapshot));
    },
    [],
  );

  const clearScheduledTechnicalFlush = useCallback(() => {
    if (technicalFlushTimeoutRef.current !== null) {
      window.clearTimeout(technicalFlushTimeoutRef.current);
      technicalFlushTimeoutRef.current = null;
    }
  }, []);

  const enqueuePendingTechnicalSnapshot = useCallback(
    async (snapshot: TrackingTechnicalSnapshot) => {
      const pendingQueueKey = getTrackingTechnicalPendingQueueStorageKey(
        snapshot.userKey,
      );
      const existingRaw = await StorageService.get(pendingQueueKey);
      const existingQueue =
        parseStoredTrackingJson<TrackingTechnicalPendingQueue>(existingRaw) ?? [];
      const updatedQueue = appendPendingTechnicalSnapshot(existingQueue, snapshot);

      await StorageService.set(pendingQueueKey, JSON.stringify(updatedQueue));
    },
    [],
  );

  const flushPendingTechnicalSnapshots = useCallback(async (currentUserKey: string) => {
    if (!currentUserKey) {
      debugTrackingLog('No se puede sincronizar snapshot tecnico: userKey vacío');
      return;
    }

    if (!isTrackingDiagnosticsSyncEnabled()) {
      debugTrackingLog('Sync de snapshot tecnico desactivado por configuración');
      return;
    }

    if (technicalSyncUnsupportedRef.current) {
      debugTrackingLog('Sync de snapshot tecnico deshabilitado: endpoint no soportado');
      return;
    }

    const pendingQueueKey = getTrackingTechnicalPendingQueueStorageKey(currentUserKey);
    const existingRaw = await StorageService.get(pendingQueueKey);
    const existingQueue =
      parseStoredTrackingJson<TrackingTechnicalPendingQueue>(existingRaw) ?? [];

    if (!existingQueue.length) return;

    debugTrackingLog('Intentando sincronizar snapshots tecnicos pendientes', {
      userKey: currentUserKey,
      pendingCount: existingQueue.length,
    });

    const failedSnapshots: TrackingTechnicalPendingQueue = [];

    for (const snapshot of existingQueue) {
      const result = await postTrackingTechnicalSnapshot(snapshot);

      if (result === 'success') continue;

      if (result === 'unsupported') {
        technicalSyncUnsupportedRef.current = true;
        await StorageService.remove(pendingQueueKey);
        debugTrackingLog(
          'Backend aun no soporta snapshot tecnico; se mantiene fallback local',
        );
        return;
      }

      failedSnapshots.push(snapshot);
    }

    if (failedSnapshots.length) {
      await StorageService.set(pendingQueueKey, JSON.stringify(failedSnapshots));
      debugTrackingLog(
        'Quedaron snapshots tecnicos pendientes de sync',
        failedSnapshots.length,
      );
      return;
    }

    await StorageService.remove(pendingQueueKey);
    debugTrackingLog('Se vació la cola de snapshots tecnicos pendientes');
  }, []);

  const schedulePendingTechnicalFlush = useCallback(
    (currentUserKey: string) => {
      if (!currentUserKey || !isTrackingDiagnosticsSyncEnabled()) return;
      if (technicalSyncUnsupportedRef.current) return;

      clearScheduledTechnicalFlush();

      technicalFlushTimeoutRef.current = window.setTimeout(() => {
        technicalFlushTimeoutRef.current = null;
        void flushPendingTechnicalSnapshots(currentUserKey);
      }, technicalProgressFlushDelayMs);
    },
    [
      clearScheduledTechnicalFlush,
      flushPendingTechnicalSnapshots,
      technicalProgressFlushDelayMs,
    ],
  );

  const persistTechnicalSnapshot = useCallback(
    async (
      session: ActiveTrackingSession,
      phase: TrackingTechnicalSnapshot['phase'],
      endedAt: string | null = null,
    ) => {
      session.diagnosticsRevision += 1;

      const technicalSnapshotKey = getTrackingTechnicalSnapshotStorageKey(
        session.userKey,
      );
      const technicalState = getTrackingTechnicalState();
      const snapshot: TrackingTechnicalSnapshot = {
        ...technicalState,
        sessionId: session.sessionId,
        revision: session.diagnosticsRevision,
        phase,
        userKey: session.userKey,
        platform: session.platform,
        appVersion: session.appVersion,
        startedAt: session.lastLoginAt,
        endedAt,
        sessionUsageMs: session.sessionUsageMs,
        permissionStatus: session.permissionStatus,
        screenCounts: session.screenCounts,
        transitionCounts: session.transitionCounts,
        mostUsedScreen: resolveMostUsedKey(session.screenCounts),
        mostUsedFlow: resolveMostUsedKey(session.transitionCounts),
        updatedAt: new Date().toISOString(),
      };

      await Promise.all([
        StorageService.set(technicalSnapshotKey, JSON.stringify(snapshot)),
        persistActiveSessionSnapshot(session),
      ]);

      if (!isTrackingDiagnosticsSyncEnabled()) {
        return;
      }

      await enqueuePendingTechnicalSnapshot(snapshot);

      if (phase === 'started' || phase === 'ended') {
        clearScheduledTechnicalFlush();
        await flushPendingTechnicalSnapshots(session.userKey);
        return;
      }

      schedulePendingTechnicalFlush(session.userKey);
    },
    [
      clearScheduledTechnicalFlush,
      enqueuePendingTechnicalSnapshot,
      flushPendingTechnicalSnapshots,
      persistActiveSessionSnapshot,
      schedulePendingTechnicalFlush,
    ],
  );

  const clearActiveSessionSnapshot = useCallback(async (currentUserKey: string) => {
    if (!currentUserKey) return;

    await StorageService.remove(getTrackingActiveSessionStorageKey(currentUserKey));
  }, []);

  const persistSessionSummary = useCallback(async (summary: TrackingSessionSummary) => {
    const aggregateKey = getTrackingAggregateStorageKey(summary.userKey);
    const lastSessionKey = getTrackingLastSessionStorageKey(summary.userKey);

    const existingRaw = await StorageService.get(aggregateKey);
    const existing = parseStoredTrackingJson<TrackingAggregate>(existingRaw);
    const merged = mergeSessionIntoAggregate(existing, summary);

    await Promise.all([
      StorageService.set(aggregateKey, JSON.stringify(merged)),
      StorageService.set(lastSessionKey, JSON.stringify(summary)),
    ]);
  }, []);

  const enqueuePendingSummary = useCallback(async (summary: TrackingSessionSummary) => {
    const pendingQueueKey = getTrackingPendingQueueStorageKey(summary.userKey);
    const existingRaw = await StorageService.get(pendingQueueKey);
    const existingQueue =
      parseStoredTrackingJson<TrackingPendingQueue>(existingRaw) ?? [];
    const updatedQueue = appendPendingTrackingSummary(existingQueue, summary);

    await StorageService.set(pendingQueueKey, JSON.stringify(updatedQueue));
  }, []);

  const flushPendingSummaries = useCallback(async (currentUserKey: string) => {
    if (!currentUserKey) {
      debugTrackingLog('No se puede sincronizar tracking: userKey vacío');
      return;
    }

    if (!isTrackingSyncEnabled()) {
      debugTrackingLog('Sync de tracking desactivado por configuración');
      return;
    }

    const pendingQueueKey = getTrackingPendingQueueStorageKey(currentUserKey);
    const existingRaw = await StorageService.get(pendingQueueKey);
    const existingQueue =
      parseStoredTrackingJson<TrackingPendingQueue>(existingRaw) ?? [];

    if (!existingQueue.length) {
      debugTrackingLog('No hay resúmenes pendientes para sincronizar', currentUserKey);
      return;
    }

    debugTrackingLog('Intentando sincronizar resúmenes pendientes', {
      userKey: currentUserKey,
      pendingCount: existingQueue.length,
    });

    const failedSummaries: TrackingPendingQueue = [];

    for (const summary of existingQueue) {
      const success = await postTrackingSessionSummary(summary);
      if (!success) {
        failedSummaries.push(summary);
      }
    }

    if (failedSummaries.length) {
      await StorageService.set(pendingQueueKey, JSON.stringify(failedSummaries));
      debugTrackingLog('Quedaron resúmenes pendientes de sync', failedSummaries.length);
      return;
    }

    await StorageService.remove(pendingQueueKey);
    debugTrackingLog('Se vació la cola de tracking pendiente');
  }, []);

  const finalizeSession = useCallback(
    async (endReason: TrackingSessionEndReason) => {
      const session = activeSessionRef.current;

      if (!session) return;

      if (session.lastForegroundAtMs) {
        session.sessionUsageMs += Math.max(Date.now() - session.lastForegroundAtMs, 0);
        session.lastForegroundAtMs = null;
      }

      const summary: TrackingSessionSummary = {
        sessionId: session.sessionId,
        userKey: session.userKey,
        platform: session.platform,
        appVersion: session.appVersion,
        lastLoginAt: session.lastLoginAt,
        sessionEndedAt: new Date().toISOString(),
        sessionUsageMs: session.sessionUsageMs,
        permissionStatus: session.permissionStatus,
        screenCounts: session.screenCounts,
        transitionCounts: session.transitionCounts,
        mostUsedScreen: resolveMostUsedKey(session.screenCounts),
        mostUsedFlow: resolveMostUsedKey(session.transitionCounts),
        endReason,
        updatedAt: new Date().toISOString(),
      };

      await persistTechnicalSnapshot(session, 'ended', summary.sessionEndedAt);
      activeSessionRef.current = null;
      resetTrackingTechnicalState();
      await Promise.all([
        persistSessionSummary(summary),
        enqueuePendingSummary(summary),
        clearActiveSessionSnapshot(summary.userKey),
      ]);
      await flushPendingSummaries(summary.userKey);
      debugTrackingLog('Sesión cerrada', summary);
    },
    [
      clearActiveSessionSnapshot,
      enqueuePendingSummary,
      flushPendingSummaries,
      persistSessionSummary,
      persistTechnicalSnapshot,
    ],
  );

  const startSession = useCallback(() => {
    if (
      !trackingReady ||
      !userKey ||
      activeSessionRef.current ||
      !isAppActiveRef.current
    ) {
      return;
    }

    const startedAt = new Date().toISOString();
    const currentScreen = mapPathnameToTrackingScreen(location.pathname);
    const initialScreenCounts = currentScreen === 'unknown' ? {} : { [currentScreen]: 1 };

    activeSessionRef.current = {
      sessionId: createTrackingId('tracking'),
      userKey,
      platform: normalizeTrackingPlatform(isNative ? Capacitor.getPlatform() : 'web'),
      appVersion: appVersionRef.current,
      lastLoginAt: startedAt,
      permissionStatus,
      sessionUsageMs: 0,
      lastForegroundAtMs: Date.now(),
      screenCounts: initialScreenCounts,
      transitionCounts: {},
      previousScreen: currentScreen === 'unknown' ? null : currentScreen,
      lastTrackedPath: location.pathname,
      diagnosticsRevision: 0,
    };
    technicalUpdateSuppressedRef.current = true;
    resetTrackingTechnicalState();
    setTrackingNetworkStatus(resolveTrackingNetworkStatus());
    setTrackingRouteState(location.pathname);
    technicalUpdateSuppressedRef.current = false;
    void persistTechnicalSnapshot(activeSessionRef.current, 'started');
    void flushPendingSummaries(userKey);
    debugTrackingLog('Sesión iniciada', {
      userKey,
      platform: activeSessionRef.current.platform,
      permissionStatus,
      pathname: location.pathname,
    });
  }, [
    trackingReady,
    userKey,
    location.pathname,
    isNative,
    permissionStatus,
    flushPendingSummaries,
    persistTechnicalSnapshot,
  ]);

  const syncBeforeLogout = useCallback(async () => {
    if (!userKey) {
      debugTrackingLog(
        'No se puede sincronizar tracking antes del logout: userKey vacío',
      );
      return;
    }

    if (activeSessionRef.current) {
      await finalizeSession('logout');
      return;
    }

    await Promise.all([
      flushPendingSummaries(userKey),
      flushPendingTechnicalSnapshots(userKey),
    ]);
  }, [finalizeSession, flushPendingSummaries, flushPendingTechnicalSnapshots, userKey]);

  useEffect(() => {
    if (!isNative) {
      appVersionRef.current = normalizeAppVersion(WEB_APP_VERSION);
      return undefined;
    }

    let cancelled = false;

    const loadAppInfo = async () => {
      try {
        const info = await App.getInfo();
        if (!cancelled) {
          appVersionRef.current = normalizeAppVersion(info.version || WEB_APP_VERSION);
          if (activeSessionRef.current) {
            activeSessionRef.current.appVersion = appVersionRef.current;
          }
        }
      } catch (error) {
        console.error('[Tracking] Error obteniendo version de app', error);
      }
    };

    void loadAppInfo();

    return () => {
      cancelled = true;
    };
  }, [isNative]);

  useEffect(
    () => registerTrackingLogoutFlushHandler(syncBeforeLogout),
    [syncBeforeLogout],
  );

  useEffect(() => {
    if (!trackingReady || !userKey || activeSessionRef.current) return;

    let cancelled = false;

    const restoreActiveSession = async () => {
      const activeSessionKey = getTrackingActiveSessionStorageKey(userKey);
      const technicalSnapshotKey = getTrackingTechnicalSnapshotStorageKey(userKey);
      const [storedSnapshotRaw, storedTechnicalSnapshotRaw] = await Promise.all([
        StorageService.get(activeSessionKey),
        StorageService.get(technicalSnapshotKey),
      ]);
      const storedSnapshot =
        parseStoredTrackingJson<ActiveTrackingSessionSnapshot>(storedSnapshotRaw);
      const storedTechnicalSnapshot = parseStoredTrackingJson<TrackingTechnicalSnapshot>(
        storedTechnicalSnapshotRaw,
      );

      if (cancelled || !storedSnapshot) {
        if (userKey) {
          await Promise.all([
            flushPendingSummaries(userKey),
            flushPendingTechnicalSnapshots(userKey),
          ]);
        }
        return;
      }

      activeSessionRef.current = {
        sessionId: storedSnapshot.sessionId,
        userKey: storedSnapshot.userKey,
        platform: storedSnapshot.platform,
        appVersion: storedSnapshot.appVersion || appVersionRef.current,
        lastLoginAt: storedSnapshot.lastLoginAt,
        permissionStatus: storedSnapshot.permissionStatus,
        sessionUsageMs: storedSnapshot.sessionUsageMs,
        lastForegroundAtMs: isAppActiveRef.current ? Date.now() : null,
        screenCounts: storedSnapshot.screenCounts,
        transitionCounts: storedSnapshot.transitionCounts,
        previousScreen: storedSnapshot.previousScreen,
        lastTrackedPath: storedSnapshot.lastTrackedPath,
        diagnosticsRevision:
          storedSnapshot.diagnosticsRevision ?? storedTechnicalSnapshot?.revision ?? 0,
      };

      technicalUpdateSuppressedRef.current = true;

      if (storedTechnicalSnapshot?.sessionId === storedSnapshot.sessionId) {
        hydrateTrackingTechnicalState({
          currentRoute: storedTechnicalSnapshot.currentRoute,
          previousRoute: storedTechnicalSnapshot.previousRoute,
          networkStatus: storedTechnicalSnapshot.networkStatus,
          lastSuccessfulAction: storedTechnicalSnapshot.lastSuccessfulAction,
          lastFailedAction: storedTechnicalSnapshot.lastFailedAction,
          recentErrors: storedTechnicalSnapshot.recentErrors,
          recentApiSuccesses: storedTechnicalSnapshot.recentApiSuccesses,
          recentApiFailures: storedTechnicalSnapshot.recentApiFailures,
        });
      } else {
        hydrateTrackingTechnicalState({
          currentRoute: storedSnapshot.lastTrackedPath ?? location.pathname,
          networkStatus: resolveTrackingNetworkStatus(),
        });
      }

      technicalUpdateSuppressedRef.current = false;

      debugTrackingLog('Sesión restaurada desde snapshot', {
        sessionId: storedSnapshot.sessionId,
        userKey: storedSnapshot.userKey,
      });

      await Promise.all([
        flushPendingSummaries(userKey),
        flushPendingTechnicalSnapshots(userKey),
      ]);
    };

    void restoreActiveSession();

    return () => {
      cancelled = true;
    };
  }, [
    trackingReady,
    userKey,
    flushPendingSummaries,
    flushPendingTechnicalSnapshots,
    location.pathname,
  ]);

  useEffect(() => {
    attachTrackingDiagnosticsDebugApi();
  }, []);

  useEffect(() => {
    window.__fielpetTracking = {
      status: () => ({
        trackingReady,
        userKey,
        technicalSyncStatus: !isTrackingDiagnosticsSyncEnabled()
          ? 'disabled'
          : technicalSyncUnsupportedRef.current
            ? 'unsupported'
            : 'enabled',
        activeSession: activeSessionRef.current ? { ...activeSessionRef.current } : null,
        technicalState: getTrackingTechnicalState(),
      }),
    };

    return () => {
      delete window.__fielpetTracking;
    };
  }, [trackingReady, userKey]);

  useEffect(() => {
    const unsubscribe = subscribeTrackingTechnicalState(() => {
      if (technicalUpdateSuppressedRef.current) return;

      const session = activeSessionRef.current;
      if (!session) return;

      void persistTechnicalSnapshot(session, 'progress');
    });

    return () => {
      unsubscribe();
    };
  }, [persistTechnicalSnapshot]);

  useEffect(() => {
    const unsubscribe = notificationTrackingEvents.on(
      NOTIFICATION_TRACKING_EVENTS.SEEN,
      (payload) => {
        if (!trackingReady || !userKey) return;

        if (!activeSessionRef.current && isAppActiveRef.current) {
          startSession();
        }

        const session = activeSessionRef.current;

        if (!session) return;

        session.transitionCounts = incrementCountBy(
          session.transitionCounts,
          NOTIFICATION_SEEN_TRACKING_KEY,
          payload.count,
        );

        technicalUpdateSuppressedRef.current = true;

        try {
          recordTrackingSuccessfulAction(buildNotificationSeenActionLabel(payload.count));
        } finally {
          technicalUpdateSuppressedRef.current = false;
        }

        void persistTechnicalSnapshot(session, 'progress');
        debugTrackingLog('Notificacion vista registrada en tracking', {
          count: payload.count,
          mode: payload.mode,
          notificationIds: payload.notificationIds,
          notificationTypes: payload.notificationTypes,
          notificationSources: payload.notificationSources,
        });
      },
    );

    return () => {
      unsubscribe();
    };
  }, [persistTechnicalSnapshot, startSession, trackingReady, userKey]);

  useEffect(() => {
    const syncNetworkStatus = () => {
      setTrackingNetworkStatus(resolveTrackingNetworkStatus());
    };

    syncNetworkStatus();
    window.addEventListener('online', syncNetworkStatus);
    window.addEventListener('offline', syncNetworkStatus);

    return () => {
      window.removeEventListener('online', syncNetworkStatus);
      window.removeEventListener('offline', syncNetworkStatus);
    };
  }, []);

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      recordTrackingRecentError({
        message: event.message || 'Unhandled window error',
        source: 'window_error',
        stack:
          typeof event.error?.stack === 'string'
            ? event.error.stack
            : typeof event.filename === 'string'
              ? `${event.filename}:${event.lineno}:${event.colno}`
              : null,
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message =
        typeof reason === 'string'
          ? reason
          : typeof reason?.message === 'string'
            ? reason.message
            : 'Unhandled promise rejection';
      const stack = typeof reason?.stack === 'string' ? reason.stack : null;

      recordTrackingRecentError({
        message,
        source: 'unhandledrejection',
        stack,
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    if (!isNative) return undefined;

    const unsubscribe = notificationPermissionEvents.on(
      NOTIFICATION_PERMISSION_EVENTS.FLOW_RESOLVED,
      (payload) => {
        const resolutionSignature = `${payload.source}:${payload.status}:${payload.prompted}`;
        const now = Date.now();
        const lastResolution = lastPermissionResolutionRef.current;
        if (
          lastResolution &&
          lastResolution.signature === resolutionSignature &&
          now - lastResolution.at < 1500
        ) {
          debugTrackingLog(
            'Se ignoró un evento duplicado de permisos',
            resolutionSignature,
          );
          return;
        }

        lastPermissionResolutionRef.current = {
          signature: resolutionSignature,
          at: now,
        };
        setPermissionStatus(payload.status);
        setTrackingEnabled(true);
        debugTrackingLog('Permiso resuelto', payload);

        if (activeSessionRef.current) {
          activeSessionRef.current.permissionStatus = payload.status;
          return;
        }

        if (userKey && isAppActiveRef.current) {
          startSession();
        }
      },
    );

    return () => {
      unsubscribe();
    };
  }, [isNative, startSession, userKey]);

  useEffect(() => {
    if (!trackingReady || !userKey) return;
    startSession();
  }, [trackingReady, userKey, startSession]);

  useEffect(() => {
    const session = activeSessionRef.current;

    if (!session || session.lastTrackedPath === location.pathname) return;

    const currentScreen = mapPathnameToTrackingScreen(location.pathname);

    if (currentScreen !== 'unknown') {
      session.screenCounts = incrementCount(session.screenCounts, currentScreen);

      if (session.previousScreen && session.previousScreen !== 'unknown') {
        const transitionKey = `${session.previousScreen}->${currentScreen}`;
        session.transitionCounts = incrementCount(
          session.transitionCounts,
          transitionKey,
        );
      }

      session.previousScreen = currentScreen;
    }

    session.lastTrackedPath = location.pathname;
    setTrackingRouteState(location.pathname);
    void persistActiveSessionSnapshot(session);
  }, [location.pathname, persistActiveSessionSnapshot]);

  useEffect(() => {
    if (!isNative) return undefined;

    let listenerHandle: { remove: () => Promise<void> } | null = null;

    const setupListener = async () => {
      listenerHandle = await App.addListener('appStateChange', ({ isActive }) => {
        isAppActiveRef.current = isActive;

        if (!trackingReady || !userKey) return;

        if (isActive) {
          startSession();
        } else {
          void finalizeSession('background');
        }
      });
    };

    void setupListener();

    return () => {
      void listenerHandle?.remove();
    };
  }, [isNative, trackingReady, userKey, startSession, finalizeSession]);

  useEffect(() => {
    if (isNative) return undefined;

    const handleVisibilityChange = () => {
      const isActive = !document.hidden;
      isAppActiveRef.current = isActive;

      if (!trackingReady || !userKey) return;

      if (isActive) {
        startSession();
      } else {
        void finalizeSession('background');
      }
    };

    const handlePageHide = () => {
      isAppActiveRef.current = false;

      if (!trackingReady || !userKey) return;

      void finalizeSession('background');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [isNative, trackingReady, userKey, startSession, finalizeSession]);

  useEffect(() => {
    return () => {
      clearScheduledTechnicalFlush();
      void finalizeSession('unmount');
    };
  }, [clearScheduledTechnicalFlush, finalizeSession]);

  useEffect(() => {
    const session = activeSessionRef.current;
    if (session) {
      session.permissionStatus = permissionStatus;
      void persistTechnicalSnapshot(session, 'progress');
    }
  }, [permissionStatus, persistTechnicalSnapshot]);

  return null;
};

export default TrackingHandler;
